import 'regenerator-runtime/runtime';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  ThhPferdVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  SpartenType,
  ThhPferdVorvertragsdaten,
  Tiergroesse,
  VertragsdatenRequestModel,
} from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { AnzahlTiereEnum, Stockmass } from '../reducers/versicherungsnehmer.reducer';
import { ThhPferdActions } from '../thhPferdActions';
import { IAppState } from '../appState';

const globalActions = new GlobalActions();

export enum VorbelegungThhPferdRequestEventsEnum {
  VORBELEGUNG_THH_PFERD_REQUEST_SUCCESSFULL = 'VORBELEGUNG_THH_PFERD_REQUEST_SUCCESSFULL',
  VORBELEGUNG_THH_PFERD_REQUEST_FAILED = 'VORBELEGUNG_THH_PFERD_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(
    GlobalActions.VORBELEGUNG_REQUESTED,
    safeSaga(vorbelegungSagaPferd, App.THH_PFERD),
  );
  yield takeEvery(
    VorbelegungThhPferdRequestEventsEnum.VORBELEGUNG_THH_PFERD_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.THH_PFERD),
  );
  yield takeEvery(
    VorbelegungThhPferdRequestEventsEnum.VORBELEGUNG_THH_PFERD_REQUEST_FAILED,
    safeSaga(errorCallback, App.THH_PFERD),
  );
}

export function* vorbelegungSagaPferd(action: GlobalAction<VorbelegungPayload>) {
  if (action.payload.appId !== App.THH_PFERD) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* pferdDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().doRequest({
        appId: App.THH_PFERD,
        body: vertragsdatenRequestModel,
        successEvent:
          VorbelegungThhPferdRequestEventsEnum.VORBELEGUNG_THH_PFERD_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungThhPferdRequestEventsEnum.VORBELEGUNG_THH_PFERD_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<ThhPferdVorvertragsdaten>) {
  const pferdVorbelegungdaten: ThhPferdVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.TIERHALTERHAFTPFLICHT_PFERD,
    successAction.payload,
  ) as ThhPferdVorbelegungdaten;
  yield* pferdDatenVorbelegen(pferdVorbelegungdaten);
}

export function* pferdDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();

  const globalPut = GlobalPut.getInstance();
  const pferdVorbelegungdaten: ThhPferdVorbelegungdaten =
    vorbelegungdaten as ThhPferdVorbelegungdaten;
  const anzahlTiere: AnzahlTiereEnum =
    pferdVorbelegungdaten.anzTiere === 1 ? AnzahlTiereEnum.EINES : AnzahlTiereEnum.MEHRERE;
  yield put(new ThhPferdActions().anzahlTiereChanged(anzahlTiere));
  if (anzahlTiere === AnzahlTiereEnum.EINES && pferdVorbelegungdaten.stockmassListe) {
    yield put(
      new ThhPferdActions().stockmassChanged(
        mapTiergrosse(pferdVorbelegungdaten.stockmassListe[0]),
      ),
    );
  }
  if (anzahlTiere === AnzahlTiereEnum.MEHRERE) {
    yield put(new ThhPferdActions().anzahlMehrerePferdeChanged(pferdVorbelegungdaten.anzTiere));
    if (pferdVorbelegungdaten.stockmassListe) {
      yield all(
        pferdVorbelegungdaten.stockmassListe.map((tiergrosse, index) =>
          put(
            new ThhPferdActions().stockmassMehrerePferdeChanged(mapTiergrosse(tiergrosse), index),
          ),
        ),
      );
    }
  }
  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }

  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.THH_PFERD,
        state.angebotsId,
      ),
    );
  }

  if (!state.vertragsoptionen?.beibehalten) {
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().navigationRequested({
        appId: App.THH_PFERD,
        subroute: '/eingabe',
      }),
    );
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().thhPferdVorbelegungFinished({
        appId: App.THH_PFERD,
        vorhandeneVorbelegungdaten: vorbelegungdaten,
      }),
    );
  } else {
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().vorbelegungForBeibehaltungRequested(pferdVorbelegungdaten),
    );
  }
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().vorbelegungFinished({ appId: App.THH_PFERD }),
  );
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().vorbelegungFinished({ appId: App.THH_PFERD }),
  );
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().navigationRequested({
      appId: App.THH_PFERD,
      subroute: '/eingabe',
    }),
  );
}

function mapTiergrosse(tiergrosse: Tiergroesse): Stockmass {
  return tiergrosse === Tiergroesse.KLEINPFERD ? Stockmass.KLEINER : Stockmass.GROESSER;
}
